import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { useEffect, useState } from "react";

import { Button } from "../components/common/Button";
import { Spinner } from "../components/common/Spinner";
import { deleteTask, downloadTaskFile, listMyTasks } from "../api/recordings";
import { useWsStore } from "../store/wsStore";
import type { RecordingTask } from "../types";

interface RunningStats {
  speed_mbps?: number;
  bytes_downloaded?: number;
  bytes_total?: number;
}

const STATUS_LABEL: Record<RecordingTask["status"], string> = {
  queued: "En cola",
  running: "Descargando",
  reconnecting: "Reconectando",
  done: "Listo",
  partial: "Parcial",
  failed: "Error",
  purged: "Purgado",
};

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

// Lee un Blob de error (responseType=blob convierte tambien errores) como JSON
// para extraer detail. Si no es JSON, devuelve el texto crudo.
async function readBlobError(blob: Blob): Promise<string> {
  try {
    const text = await blob.text();
    try {
      const j = JSON.parse(text);
      if (j && typeof j.detail === "string") return j.detail;
    } catch {
      /* not json */
    }
    return text.slice(0, 200);
  } catch {
    return "";
  }
}

export default function DownloadsPage() {
  const qc = useQueryClient();
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [running, setRunning] = useState<Record<number, RunningStats>>({});
  const { data, isLoading } = useQuery({
    queryKey: ["recordings"],
    queryFn: listMyTasks,
    refetchInterval: 3000,
  });

  const subscribe = useWsStore((s) => s.subscribe);
  useEffect(() => {
    return subscribe((msg) => {
      if (msg.type !== "download.update" && msg.type !== "download.done") return;
      const p = (msg.payload as Record<string, unknown> | undefined) ?? {};
      const taskId = typeof p.task_id === "number" ? p.task_id : null;
      if (taskId !== null) {
        const speed = typeof p.speed_mbps === "number" ? p.speed_mbps : undefined;
        const bd = typeof p.bytes_downloaded === "number" ? p.bytes_downloaded : undefined;
        const bt = typeof p.bytes_total === "number" ? p.bytes_total : undefined;
        if (speed !== undefined || bd !== undefined || bt !== undefined) {
          setRunning((prev) => ({
            ...prev,
            [taskId]: {
              speed_mbps: speed ?? prev[taskId]?.speed_mbps,
              bytes_downloaded: bd ?? prev[taskId]?.bytes_downloaded,
              bytes_total: bt ?? prev[taskId]?.bytes_total,
            },
          }));
        }
        if (msg.type === "download.done" || p.status === "failed"
            || p.status === "purged" || p.status === "partial") {
          setRunning((prev) => {
            const next = { ...prev };
            delete next[taskId];
            return next;
          });
        }
      }
      qc.invalidateQueries({ queryKey: ["recordings"] });
    });
  }, [subscribe, qc]);

  const onDelete = async (id: number) => {
    if (!confirm("Eliminar tarea de descarga?")) return;
    try { await deleteTask(id); qc.invalidateQueries({ queryKey: ["recordings"] }); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const onDownload = async (id: number) => {
    setDownloadingId(id);
    let objectUrl: string | null = null;
    let anchor: HTMLAnchorElement | null = null;
    try {
      const { blob, filename } = await downloadTaskFile(id);
      if (!blob || blob.size === 0) {
        toast.error("El archivo descargado está vacío");
        return;
      }
      objectUrl = URL.createObjectURL(blob);
      anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = filename;
      anchor.rel = "noopener";
      anchor.style.display = "none";
      document.body.appendChild(anchor);
      anchor.click();
      toast.success(`Descargando ${filename} (${formatBytes(blob.size)})`);
      // Mantener anchor + objectURL vivos un rato para que el browser
      // efectivamente arranque la descarga antes de limpiar.
      const a = anchor;
      const url = objectUrl;
      anchor = null;
      objectUrl = null;
      window.setTimeout(() => {
        try { a.remove(); } catch { /* noop */ }
        URL.revokeObjectURL(url);
      }, 5000);
    } catch (e: any) {
      // Cuando responseType=blob, e.response.data es un Blob, no JSON.
      // Leerlo manualmente para extraer detail.
      let msg = "No se pudo descargar el archivo";
      if (e?.code === "ECONNABORTED" || e?.message?.includes("timeout")) {
        msg = "La descarga tardó demasiado y fue cancelada por timeout";
      } else if (e?.code === "ERR_NETWORK" || e?.message === "Network Error") {
        msg = "Error de red al descargar — revisá tu conexión";
      } else {
        const d = e?.response?.data;
        if (d instanceof Blob) {
          const txt = await readBlobError(d);
          if (txt) msg = txt;
        } else if (typeof d?.detail === "string") {
          msg = d.detail;
        } else if (e?.message) {
          msg = `${msg}: ${e.message}`;
        }
      }
      toast.error(msg);
    } finally {
      setDownloadingId(null);
    }
  };

  if (isLoading) return <div className="p-6"><Spinner /></div>;

  return (
    <div className="p-4">
      <h1 className="text-lg font-semibold mb-4">Mis descargas</h1>
      <div className="border border-line rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-panel border-b border-line">
            <tr className="text-xs text-muted">
              <th className="text-left px-3 py-2">#</th>
              <th className="text-left px-3 py-2">Dispositivo</th>
              <th className="text-left px-3 py-2">CH</th>
              <th className="text-left px-3 py-2">Inicio</th>
              <th className="text-left px-3 py-2">Fin</th>
              <th className="text-left px-3 py-2">Formato</th>
              <th className="text-left px-3 py-2">Estado</th>
              <th className="text-left px-3 py-2">Progreso</th>
              <th className="text-left px-3 py-2">Velocidad</th>
              <th className="text-right px-3 py-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {data && data.length === 0 && (
              <tr><td colSpan={10} className="text-center text-muted py-6">Sin descargas.</td></tr>
            )}
            {data?.map((t) => {
              const stats = running[t.id];
              return (
                <tr key={t.id} className="border-b border-line hover:bg-panel">
                  <td className="px-3 py-1.5">{t.id}</td>
                  <td className="px-3 py-1.5">{t.device_id}</td>
                  <td className="px-3 py-1.5">{t.channel}</td>
                  <td className="px-3 py-1.5 text-muted">{new Date(t.start_at).toLocaleString()}</td>
                  <td className="px-3 py-1.5 text-muted">{new Date(t.end_at).toLocaleString()}</td>
                  <td className="px-3 py-1.5 uppercase">{t.format}</td>
                  <td className="px-3 py-1.5">
                    <span className={
                      t.status === "done" ? "text-green-400" :
                      t.status === "failed" ? "text-red-400" :
                      t.status === "running" ? "text-blue-400" :
                      t.status === "reconnecting" ? "text-amber-400" :
                      t.status === "partial" ? "text-orange-400" :
                      t.status === "purged" ? "text-amber-400" : "text-muted"
                    }>
                      {STATUS_LABEL[t.status] ?? t.status}
                    </span>
                    {t.error_message && (
                      <div className={
                        (t.status === "reconnecting" ? "text-amber-400" :
                         t.status === "partial" ? "text-orange-400" : "text-red-400")
                        + " text-xs"
                      }>{t.error_message}</div>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-32 h-2 bg-line rounded overflow-hidden">
                        <div className="h-full bg-accent" style={{ width: `${t.progress_pct}%` }} />
                      </div>
                      <span className="text-xs text-muted w-9 tabular-nums">{t.progress_pct}%</span>
                    </div>
                    {t.status === "running" && stats?.bytes_downloaded !== undefined && (
                      <div className="text-xs text-muted mt-0.5">
                        {formatBytes(stats.bytes_downloaded)}
                        {stats.bytes_total ? ` / ${formatBytes(stats.bytes_total)}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-xs tabular-nums">
                    {t.status === "running" && stats?.speed_mbps !== undefined ? (
                      <span className="text-blue-400">{stats.speed_mbps.toFixed(2)} MB/s</span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right space-x-1">
                    {(t.status === "done" || t.status === "partial") && (
                      <Button
                        variant={t.status === "partial" ? "secondary" : "primary"}
                        size="sm"
                        loading={downloadingId === t.id}
                        onClick={() => onDownload(t.id)}
                        title={t.status === "partial"
                          ? "Descargar lo que se alcanzó a obtener antes de perder el enlace"
                          : undefined}
                      >
                        <Download size={12} /> {t.status === "partial" ? "Descargar parcial" : "Descargar"}
                      </Button>
                    )}
                    {t.status === "purged" && (
                      <span className="text-xs text-muted italic" title="Excedió la cuota FIFO de 5 descargas por usuario">
                        Eliminado del servidor
                      </span>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => onDelete(t.id)}><Trash2 size={12} /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
